import { useMutation, useQuery } from '@tanstack/react-query'

import type {
    AccountSingleStepPinRequest,
    AddressDetailsRequest,
    CancelTransactionRequest,
    ChangePinRequest,
    ContractPreliminaryRequest,
    ContractSignOrBuyRequest,
    CreatePinRequest,
    EmploymentInfoRequest,
    FinancialDetailsRequestV2,
    FinancialProductV2Request,
    GetSelfCheckoutTokenRequest,
    JWTLoginRequest,
    MaritalStatusRequest,
    NationalityCodebookDTO,
    PaymentDataRequest,
    PersonalDetailsV2Request,
    PersonMarketingConsentChangeRequest,
    ProfileAddressChangeRequest,
    ProfileIbanChangeRequest,
    ReCreatePinRequest,
    RequestFinancialDataRequest,
    SecurityConfirmationRequest,
    SettlementCodebookDTO,
    TransactionRequest,
    ValidationDataV2DTO,
    VendorProductValidationRequest,
    VendorRequestInvalidateRequest,
} from '@leanpay/models'
import type { KYCSubmitCheckPayload } from '@leanpay/services'
import { VendorRequestType } from '@leanpay/models'
import {
    createStandardRetryHandler,
    STANDARD_RETRY_DELAY_MS,
    useGetCountries as useGetCountriesShared,
    useGetPostZipsByCountry as useGetPostZipsByCountryShared,
} from '@leanpay/query-hooks'
import {
    checkoutService,
    downloadService,
    getAuthToken,
    kycService,
    saveBlobFile,
    setAuthToken,
} from '@leanpay/services'
import { SelectOptionOther } from '@leanpay/utils'

import { queryClient } from 'app/app'
import { useAppStore } from 'app/app-store'
import { CacheKey, countryCodebookId, isMarketHun } from 'library/constants'

/**
 * Reference-data and codebook hooks shared with checkout and pre-qualify.
 * Only hooks that need this app's market constant are wrapped here.
 */
export {
    useGetCurrency,
    useGetCurrentTime,
    useGetEmploymentTypes,
    useGetIndustriesOfEmployment,
    useGetMaritalStatuses,
    useGetPostZipsBySettlement,
    useGetSettlementsByPostZip,
    useGetSettlementsByStreet,
    useGetStreetsByPostZip,
    useGetStreetsBySettlement,
    useLoadMaritalStatus,
} from '@leanpay/query-hooks'

export const useGetCountries = () => useGetCountriesShared(countryCodebookId)
export const useGetPostZipsByCountry = () => useGetPostZipsByCountryShared(countryCodebookId)

export const useGetVendorTokenCheckout = () => {
    const mutation = useMutation({
        mutationFn: (vendorMockToken: string) =>
            checkoutService.getVendorTokenCheckout(vendorMockToken),
    })

    return mutation
}

export const useInitPreQualifyVendor = () => {
    const { vendor, productCode, vendorToken } = useAppStore()

    return useQuery({
        queryKey: [CacheKey.PreQualifyVendorInit, vendor, productCode],
        // A token in the session means the vendor request already exists on the BE.
        enabled: !vendorToken,
        queryFn: async () => {
            const { token } = await checkoutService.getVendorTokenPQ({
                vendor,
                productCode,
                requestType: VendorRequestType.SELF_CHECKOUT,
            })
            if (!token) {
                throw new Error('DATA_NOT_VALID')
            }

            return token
        },
        // Every call creates a new vendor request on the BE, so it must never repeat by itself.
        retry: false,
        retryOnMount: false,
        staleTime: Infinity,
    })
}

export const useGetPreQualifiedInfo = () => {
    const { vendorToken, vendorData } = useAppStore()

    return useQuery({
        queryKey: [CacheKey.PreQualifiedInfo, vendorToken],
        enabled: !!vendorToken && !vendorData,
        queryFn: () => checkoutService.getPreQualifiedInfo({ token: vendorToken }),
        retry: false,
        staleTime: Infinity,
    })
}

export const useGetPaymentData = () => {
    const mutation = useMutation({
        mutationFn: (data: PaymentDataRequest) => {
            return checkoutService.getPaymentData(data)
        },
    })

    return mutation
}

export const useDownloadInitialOffer = () => {
    const mutation = useMutation({
        mutationFn: (data: TransactionRequest) => downloadService.downloadInitialOffer(data),
        onSuccess: (data: { blob: Blob; fileName: string }) =>
            saveBlobFile(data.blob, data.fileName),
    })

    return mutation
}
export const useDownloadPreliminaryContract = () => {
    const mutation = useMutation({
        mutationFn: (data: ContractPreliminaryRequest) =>
            downloadService.downloadPreliminaryContract(data),
        onSuccess: (data: { blob: Blob; fileName: string }) =>
            saveBlobFile(data.blob, data.fileName),
    })

    return mutation
}

export const useDownloadPrecontractualPeriodWaiver = () => {
    const mutation = useMutation({
        mutationFn: (data: ContractPreliminaryRequest) =>
            downloadService.downloadPrecontractualPeriodWaiver(data),
        onSuccess: (data: { blob: Blob; fileName: string }) =>
            saveBlobFile(data.blob, data.fileName),
    })

    return mutation
}

export const useRequestPIN = () => {
    const mutation = useMutation({
        mutationFn: async (data: AccountSingleStepPinRequest) => {
            return checkoutService.requestPIN(data)
        },
    })

    return mutation
}

export const useResendOtpOverInvoice = () => {
    const mutation = useMutation({
        mutationFn: (phoneNumber: string) => {
            return checkoutService.resendOtpOverVoice(phoneNumber)
        },
    })

    return mutation
}

export const useGetSelfCheckoutToken = () => {
    const mutation = useMutation({
        mutationFn: (request: GetSelfCheckoutTokenRequest) => {
            return checkoutService.getSelfCheckoutToken(request)
        },
    })

    return mutation
}

export const useResendOtp = () => {
    const mutation = useMutation({
        mutationFn: (phoneNumber: string) => {
            return checkoutService.resendOtp(phoneNumber)
        },
    })

    return mutation
}

export const useMutateTriggerWatchList = () => {
    const { vendorToken } = useAppStore()
    const mutation = useMutation({
        mutationFn: () => checkoutService.triggerWatchListCheck(vendorToken),
    })

    return mutation
}

export const useUpdateIban = () => {
    const mutation = useMutation({
        mutationFn: (payload: ProfileIbanChangeRequest) => checkoutService.ibanUpdate(payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}
export const useUpdateAddress = () => {
    const mutation = useMutation({
        mutationFn: (payload: ProfileAddressChangeRequest) =>
            checkoutService.addressUpdate(payload),

        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}
export const useUpdateMarketingConsent = () => {
    const mutation = useMutation({
        mutationFn: (payload: PersonMarketingConsentChangeRequest) =>
            checkoutService.changeMarketingConsentLoginAction(payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}

export const useAuthenticate = () => {
    const { setIsAuthenticated } = useAppStore()
    const mutation = useMutation({
        mutationFn: async (data: JWTLoginRequest) => {
            return checkoutService.authenticate(data)
        },
        onSuccess: async (res) => {
            setAuthToken(res.idToken)
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })

            setIsAuthenticated(true)
        },
    })

    return mutation
}

export const useSubmitPersonalDetails = () => {
    const mutation = useMutation({
        mutationFn: (data: PersonalDetailsV2Request) => {
            return checkoutService.submitPersonalDetails(data)
        },
        retry: createStandardRetryHandler(),
        retryDelay: STANDARD_RETRY_DELAY_MS,
    })

    return mutation
}
export const useSubmitAddressDetails = () => {
    const mutation = useMutation({
        mutationFn: (data: AddressDetailsRequest) => {
            return checkoutService.submitAddressDetailsV2(data)
        },
        retry: createStandardRetryHandler(),
        retryDelay: STANDARD_RETRY_DELAY_MS,
    })

    return mutation
}

export const useGetNationalities = () => {
    return useQuery({
        queryKey: [CacheKey.Nationality],
        queryFn: () => checkoutService.getNationalityCodebook(),
        select: (nationalities: any[]) => {
            const targetedNationality = nationalities.find(
                (n: NationalityCodebookDTO) => n.country.toString().toUpperCase() === 'HUNGARY',
            )
            const sortedNationalites = targetedNationality
                ? [
                      targetedNationality,
                      ...nationalities.filter(
                          (n: NationalityCodebookDTO) =>
                              n.country.toString().toUpperCase() !== 'HUNGARY',
                      ),
                  ]
                : nationalities
            return sortedNationalites
        },
    })
}

export const useGetLoanAmountLimits = (vendorId: string) => {
    return useQuery({
        queryKey: [CacheKey.LoanAmountLimits, vendorId],
        queryFn: () => checkoutService.getLoanAmountLimits({ vendorId }),
    })
}

export const useLoadPersonalDetails = () => {
    const { vendorToken } = useAppStore()
    const mutation = useMutation({
        mutationFn: () => checkoutService.loadPersonalDetailsData(vendorToken),
    })

    return mutation
}

export const useLoadAddressDetails = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.AddressDetails],
        queryFn: async () => checkoutService.loadAddressDetails(vendorToken),
        retry: createStandardRetryHandler(),
    })
}

export const useGetSettlements = () => {
    return useQuery({
        queryKey: [CacheKey.Settlement, countryCodebookId],
        enabled: !!countryCodebookId,
        queryFn: async () => {
            if (isMarketHun) {
                const settlementOptions = await checkoutService.getSettlements(countryCodebookId)
                const data = [
                    ...settlementOptions,
                    {
                        id: 0,
                        name: SelectOptionOther.OtherHUN,
                        code: '0',
                    } as SettlementCodebookDTO,
                ]
                return data
            }
            return checkoutService.getSettlements(countryCodebookId)
        },
    })
}
export const useRequestFinancialData = () => {
    const mutation = useMutation({
        mutationFn: (data: RequestFinancialDataRequest) =>
            checkoutService.requestFinancialData(data),
    })

    return mutation
}

export const useValidateVendorProduct = () => {
    const mutation = useMutation({
        mutationFn: (data: VendorProductValidationRequest) =>
            checkoutService.validateVendorProduct(data),
    })

    return mutation
}

export const useCreatePin = () => {
    const mutation = useMutation({
        mutationFn: (payload: CreatePinRequest) => {
            return checkoutService.createPin(payload)
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}
export const useRecreatePin = () => {
    const mutation = useMutation({
        mutationFn: (payload: ReCreatePinRequest) => checkoutService.recreatePin(payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}
export const useChangePin = () => {
    const mutation = useMutation({
        mutationFn: (payload: ChangePinRequest) => checkoutService.changePin(payload),
    })

    return mutation
}

export const useSubmitMartialStatus = () => {
    const mutation = useMutation({
        mutationFn: (payload: MaritalStatusRequest) => checkoutService.submitMaritalStatus(payload),
        retry: createStandardRetryHandler(),
        retryDelay: STANDARD_RETRY_DELAY_MS,
    })

    return mutation
}

export const useSubmitEmploymentInfo = () => {
    const mutation = useMutation({
        mutationFn: (payload: EmploymentInfoRequest) =>
            checkoutService.submitEmploymentInfo(payload),
        retry: createStandardRetryHandler(),
        retryDelay: STANDARD_RETRY_DELAY_MS,
    })

    return mutation
}

export const useValidateOfferV2 = () => {
    const mutation = useMutation({
        mutationFn: (data: ValidationDataV2DTO) => {
            return checkoutService.validateCheckoutOfferV2(data)
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}

export const useGetContractData = () => {
    const mutation = useMutation({
        mutationFn: (payload: TransactionRequest) => checkoutService.getContractData(payload),
    })

    return mutation
}

export const useGetPepQuestionnaireROM = () => {
    return useQuery({
        queryKey: [CacheKey.PEPQuestionnaire],
        queryFn: () => checkoutService.getPepQuestionnaireROM(),
    })
}

export const useGetSessionInfo = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        // getAuthToken() guard matches checkout/pre-qualify/credit: without it the
        // request fires unauthenticated as soon as a vendorToken exists.
        enabled: !!vendorToken && !!getAuthToken(),
        queryKey: [CacheKey.SessionInfo],
        queryFn: () => checkoutService.getSessionInfo(),
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

export const useSignOrBuy = () => {
    const mutation = useMutation({
        mutationFn: ({
            payload,
            twoFaPin,
        }: {
            payload: ContractSignOrBuyRequest
            twoFaPin?: string
        }) => checkoutService.signOrBuy(payload, twoFaPin),
    })

    return mutation
}

export const useInvalidateVendorRequest = () => {
    const mutation = useMutation({
        mutationFn: (payload: VendorRequestInvalidateRequest) =>
            checkoutService.invalidateVendorRequest(payload),
    })

    return mutation
}

export const useGetSdkToken = () => {
    const { vendorToken } = useAppStore()
    const mutation = useMutation({
        mutationFn: () => kycService.getSdkToken(vendorToken),
    })

    return mutation
}

export const useKYCSubmitCheck = () => {
    const mutation = useMutation({
        mutationFn: (payload: KYCSubmitCheckPayload) => kycService.submitCheck(payload),
    })

    return mutation
}
export const useInitFinancialProduct = () => {
    const mutation = useMutation({
        mutationFn: (request: FinancialProductV2Request) =>
            checkoutService.financialProductInit(request),
        retry: createStandardRetryHandler(),
        retryDelay: STANDARD_RETRY_DELAY_MS,
    })

    return mutation
}

export const useSubmitFinancialDetails = () => {
    const mutation = useMutation({
        mutationFn: (request: FinancialDetailsRequestV2) =>
            checkoutService.submitFinancialDetails(request),
    })

    return mutation
}

export const useConfirmAuthorizationCodePrompt = () => {
    const mutation = useMutation({
        mutationFn: (request: SecurityConfirmationRequest) =>
            checkoutService.confirmAuthorizationCodePrompt(request),
    })

    return mutation
}

export const useGetAuthorizationCode = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.AuthorizationCode],
        queryFn: () => checkoutService.getAuthorizationCode(vendorToken),
    })
}

export const useGetAuthorizationCodeStatusData = (isEnabled = true, refetchInterval = 5000) => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.AuthorizationCodeStatus],
        refetchInterval: refetchInterval,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        enabled: isEnabled,
        queryFn: () => checkoutService.getAuthorizationCodeStatus(vendorToken),
    })
}

export const useCancelTransaction = () => {
    const mutation = useMutation({
        mutationFn: (data: CancelTransactionRequest) => {
            return checkoutService.cancelTransaction(data)
        },
    })

    return mutation
}
